import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthSearchOpsTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { GrowthContentAssetSelectorComponent } from '../../../shared/design-system/growth-content-asset-selector.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { OfferingSelectorComponent } from '../../../shared/design-system/offering-selector.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import {
  SEARCH_OPS_URL_TEMPLATES,
  SearchChangeLogDto,
  SearchChecklistTemplateDto,
  SearchCrawlRunDto,
  SearchOnpageQaResultDto,
  SearchPropertyListItemDto,
  SearchUrlInventoryDto
} from './growth-search-ops.models';
import { GrowthSearchOpsService } from './growth-search-ops.service';

@Component({
  standalone: true,
  selector: 'ain-growth-search-ops-urls',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TableModule,
    RouterLink,
    OfferingSelectorComponent,
    GrowthContentAssetSelectorComponent,
    DatePipe
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.searchops.urls.title')"
      [description]="t('growth.searchops.urls.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.searchops.urls.help.title'"
        [descriptionKey]="'growth.searchops.urls.help.description'"
        [bulletKeys]="[
          'growth.searchops.urls.help.bullets.inventory',
          'growth.searchops.urls.help.bullets.crawl',
          'growth.searchops.urls.help.bullets.qa'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load(true)"></button>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.backlog.actions.workHome')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/issues']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.issues')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/redirects']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.redirects')"></a>
        <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error()" />
      } @else if (properties().length === 0) {
        <ain-state type="empty" [title]="t('growth.searchops.workHome.empty.title')" [message]="t('growth.searchops.workHome.empty.message')" />
      } @else {
        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.fields.property') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.urls.propertyHelp.title'"
              [descriptionKey]="'growth.searchops.urls.propertyHelp.description'" />
          </div>
          <ain-form-field [label]="t('growth.searchops.fields.property')">
            <p-select
              [options]="propertyOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedPropertyId"
              appendTo="body"
              (onChange)="onPropertyChange($event.value)" />
          </ain-form-field>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.urls.sections.inventory') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.urls.inventoryHelp.title'"
              [descriptionKey]="'growth.searchops.urls.inventoryHelp.description'" />
          </div>
          <div class="toolbar">
            <button pButton type="button" [label]="t('growth.searchops.urls.actions.addUrl')" (click)="urlDialogOpen = true"></button>
            <button pButton type="button" severity="secondary" [disabled]="busy()" [label]="t('growth.searchops.urls.actions.sandboxCrawl')" (click)="runCrawl()"></button>
          </div>
          @if (crawlSummary()) {
            <p class="summary">{{ crawlSummary() }}</p>
          }
          <p-table [value]="urls()" [paginator]="true" [rows]="20" dataKey="id">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.url') }}</th>
                <th>{{ t('growth.searchops.fields.template') }}</th>
                <th>{{ t('growth.searchops.fields.httpStatus') }}</th>
                <th>{{ t('growth.searchops.fields.indexable') }}</th>
                <th>{{ t('growth.searchops.fields.seoProfile') }}</th>
                <th>{{ t('growth.searchops.fields.actions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td class="wrap-url">{{ row.url }}</td>
                <td>{{ t('growth.searchops.urlTemplate.' + row.template) }}</td>
                <td>{{ row.httpStatus ?? '—' }}</td>
                <td>{{ row.indexable ? t('common.yes') : t('common.no') }}</td>
                <td>
                  @if (row.seoProfileId) {
                    <a [routerLink]="['/growth/seo/profiles', row.seoProfileId]">{{ t('growth.searchops.urls.openProfile') }}</a>
                  } @else {
                    —
                  }
                </td>
                <td class="actions">
                  <button pButton type="button" size="small" severity="secondary" [label]="t('growth.searchops.urls.actions.onpageQa')" (click)="openQa(row)"></button>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="6">{{ t('growth.searchops.urls.empty') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.urls.sections.changelog') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.urls.changelogHelp.title'"
              [descriptionKey]="'growth.searchops.urls.changelogHelp.description'" />
          </div>
          <p-table [value]="changelogs()" [paginator]="true" [rows]="10" dataKey="id">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.occurredAt') }}</th>
                <th>{{ t('growth.searchops.fields.summary') }}</th>
                <th>{{ t('growth.searchops.fields.source') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.occurredAtUtc | date:'short' }}</td>
                <td>{{ row.summary }}</td>
                <td>{{ t('growth.searchops.changelogSource.' + row.source) }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="3">{{ t('growth.searchops.urls.emptyChangelog') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>

    <p-dialog
      [header]="t('growth.searchops.urls.dialogs.addUrl')"
      [(visible)]="urlDialogOpen"
      [modal]="true"
      [style]="{ width: '32rem' }"
      appendTo="body">
      <ain-info-hint
        [titleKey]="'growth.searchops.urls.urlDialogHelp.title'"
        [descriptionKey]="'growth.searchops.urls.urlDialogHelp.description'" />
      <ain-form-field [label]="t('growth.searchops.fields.url')">
        <input pInputText [(ngModel)]="urlModel.url" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.canonical')">
        <input pInputText [(ngModel)]="urlModel.canonicalUrl" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.template')">
        <p-select [options]="templateOptions()" optionLabel="label" optionValue="value" [(ngModel)]="urlModel.template" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.contentAsset')">
        <ain-growth-content-asset-selector [(ngModel)]="urlModel.contentAssetId" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.offering')">
        <ain-offering-selector [(ngModel)]="urlModel.offeringId" />
      </ain-form-field>
      <div class="check-row">
        <p-checkbox [(ngModel)]="urlModel.indexable" [binary]="true" inputId="url-indexable" />
        <label for="url-indexable">{{ t('growth.searchops.fields.indexable') }}</label>
      </div>
      <div class="toolbar">
        <button pButton type="button" [disabled]="busy()" [label]="t('common.save')" (click)="saveUrl()"></button>
      </div>
    </p-dialog>

    <p-dialog
      [header]="t('growth.searchops.urls.dialogs.onpageQa')"
      [(visible)]="qaDialogOpen"
      [modal]="true"
      [style]="{ width: '36rem' }"
      appendTo="body">
      <ain-info-hint
        [titleKey]="'growth.searchops.urls.qaHelp.title'"
        [descriptionKey]="'growth.searchops.urls.qaHelp.description'" />
      @if (onpageTemplate(); as template) {
        @for (item of template.items; track item.key) {
          <div class="check-row">
            <p-checkbox [(ngModel)]="qaChecks[item.key]" [binary]="true" [inputId]="'qa-' + item.key" />
            <label [for]="'qa-' + item.key">{{ item.label }}</label>
          </div>
        }
      }
      <ain-form-field [label]="t('growth.searchops.fields.pageTitle')">
        <input pInputText [(ngModel)]="qaTitle" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.metaDescription')">
        <input pInputText [(ngModel)]="qaDescription" />
      </ain-form-field>
      <div class="check-row">
        <p-checkbox [(ngModel)]="qaCreateApply" [binary]="true" inputId="qa-apply" />
        <label for="qa-apply">{{ t('growth.searchops.urls.actions.draftApply') }}</label>
      </div>
          @if (qaResult(); as result) {
        <p class="summary">
          {{ qaResultLabel(result) }}
          @if (result.seoProfileId) {
            · <a [routerLink]="['/growth/seo/profiles', result.seoProfileId]">{{ t('growth.searchops.urls.openProfile') }}</a>
          }
        </p>
      }
      <div class="toolbar">
        <button pButton type="button" [disabled]="busy()" [label]="t('growth.searchops.urls.actions.runQa')" (click)="runQa()"></button>
      </div>
    </p-dialog>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 12px; }
    .card { background: var(--ain-color-surface, #fff); padding: 16px; border-radius: 12px; margin-bottom: 12px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
    .section-title h3 { margin: 0; font-size: 1rem; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
    .actions { display: flex; flex-wrap: wrap; gap: 6px; }
    .check-row { display: flex; align-items: center; gap: 8px; margin: 6px 0; }
    .summary { color: #64748b; overflow-wrap: anywhere; }
    .wrap-url { overflow-wrap: anywhere; }
  `]
})
export class GrowthSearchOpsUrlsComponent implements OnInit {
  private readonly api = inject(GrowthSearchOpsService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly properties = signal<SearchPropertyListItemDto[]>([]);
  readonly urls = signal<SearchUrlInventoryDto[]>([]);
  readonly crawls = signal<SearchCrawlRunDto[]>([]);
  readonly changelogs = signal<SearchChangeLogDto[]>([]);
  readonly crawlSummary = signal<string | null>(null);
  readonly onpageTemplate = signal<SearchChecklistTemplateDto | null>(null);
  readonly qaResult = signal<SearchOnpageQaResultDto | null>(null);
  selectedPropertyId: string | null = null;
  urlDialogOpen = false;
  qaDialogOpen = false;
  urlModel = { url: '', canonicalUrl: '', template: 'other', indexable: true, contentAssetId: null as string | null, offeringId: null as string | null };
  qaChecks: Record<string, boolean> = {};
  qaTitle = '';
  qaDescription = '';
  qaCreateApply = true;
  qaUrlId: string | null = null;
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event.eventType === LiveUpdateEventTypes.growthSearchOpsChanged) {
          this.load(false);
        }
      });
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    this.api.listChecklists().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: templates => this.onpageTemplate.set(templates.find(item => item.key === 'onpage') ?? null)
    });
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.selectedPropertyId = params.get('propertyId');
      this.load(true);
    });
    void this.setupLiveSubscription();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  qaResultLabel(result: SearchOnpageQaResultDto): string {
    return this.t('growth.searchops.urls.qaResult').replace('{score}', String(result.score));
  }

  propertyOptions() {
    return this.properties().map(item => ({ value: item.id, label: `${item.name} (${item.primaryDomain})` }));
  }

  templateOptions() {
    return SEARCH_OPS_URL_TEMPLATES.map(value => ({ value, label: this.t('growth.searchops.urlTemplate.' + value) }));
  }

  propertyQuery() {
    return this.selectedPropertyId ? { propertyId: this.selectedPropertyId } : {};
  }

  onPropertyChange(propertyId: string): void {
    void this.router.navigate([], { relativeTo: this.route, queryParams: { propertyId }, queryParamsHandling: 'merge' });
  }

  load(showSpinner: boolean): void {
    if (showSpinner) {
      this.loading.set(true);
    }
    this.error.set('');
    this.api.listProperties().pipe(finalize(() => this.loading.set(false))).subscribe({
      next: properties => {
        this.properties.set(properties);
        if (!this.selectedPropertyId && properties.length > 0) {
          this.selectedPropertyId = properties[0].id;
        }
        this.loadBoard();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  saveUrl(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    this.busy.set(true);
    this.api.upsertUrl(this.selectedPropertyId, {
      url: this.urlModel.url,
      canonicalUrl: this.urlModel.canonicalUrl || null,
      template: this.urlModel.template,
      indexable: this.urlModel.indexable,
      contentAssetId: this.urlModel.contentAssetId,
      offeringId: this.urlModel.offeringId
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => {
        this.urlDialogOpen = false;
        this.loadBoard();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  runCrawl(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    this.busy.set(true);
    this.api.runCrawl(this.selectedPropertyId).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: crawl => {
        this.crawlSummary.set(this.t('growth.searchops.urls.crawlResult').replace('{count}', String(crawl.issueCount)));
        this.loadBoard();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  openQa(row: SearchUrlInventoryDto): void {
    this.qaUrlId = row.id;
    this.qaTitle = '';
    this.qaDescription = '';
    this.qaCreateApply = true;
    this.qaResult.set(null);
    this.qaChecks = {};
    this.onpageTemplate()?.items.forEach(item => this.qaChecks[item.key] = false);
    this.qaDialogOpen = true;
  }

  runQa(): void {
    if (!this.qaUrlId) {
      return;
    }
    const template = this.onpageTemplate();
    const results = (template?.items ?? []).map(item => ({ key: item.key, checked: !!this.qaChecks[item.key] }));
    this.busy.set(true);
    this.api.runOnpageQa(this.qaUrlId, {
      resultsJson: JSON.stringify(results),
      title: this.qaTitle || null,
      description: this.qaDescription || null,
      createApplyJob: this.qaCreateApply
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: result => {
        this.qaResult.set(result);
        this.loadBoard();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  private loadBoard(): void {
    if (!this.selectedPropertyId) {
      this.urls.set([]);
      this.crawls.set([]);
      this.changelogs.set([]);
      return;
    }
    forkJoin({
      urls: this.api.listUrls(this.selectedPropertyId),
      crawls: this.api.listCrawls(this.selectedPropertyId),
      changelogs: this.api.listChangeLogs(this.selectedPropertyId)
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: board => {
        this.urls.set(board.urls);
        this.crawls.set(board.crawls);
        this.changelogs.set(board.changelogs);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthSearchOpsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
